'use client';

import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { FileText, Type, Loader2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslations } from 'next-intl';
import { getUserSettings, updateUserSettings } from '@/app/[locale]/actions/settings';
import { VscMarkdown } from "react-icons/vsc";

interface MarkdownToggleButtonProps {
  disabled?: boolean;
  onToggle?: (isMarkdown: boolean) => void;
}

export function MarkdownToggleButton({ disabled, onToggle }: MarkdownToggleButtonProps) {
  const [isMarkdown, setIsMarkdown] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const t = useTranslations('markdownToggle');

  // Load user settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await getUserSettings('outputPreferences');
        const markdownEnabled = settings?.enableMarkdown ?? false;
        setIsMarkdown(markdownEnabled);
        onToggle?.(markdownEnabled);
      } catch (error) {
        console.error('Failed to load markdown setting:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [onToggle]);

  const handleToggle = async () => {
    if (disabled || isLoading) return;

    const newValue = !isMarkdown;
    setIsMarkdown(newValue);
    onToggle?.(newValue);

    try {
      await updateUserSettings('outputPreferences', {
        enableMarkdown: newValue
      });
    } catch (error) {
      console.error('Failed to save markdown setting:', error);
      // Revert on error
      setIsMarkdown(!newValue);
      onToggle?.(!newValue);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={isMarkdown ? "default" : "outline"}
            size="icon"
            onClick={handleToggle}
            disabled={disabled || isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isMarkdown ? (
              <VscMarkdown className="h-4 w-4" />
            ) : (
              <VscMarkdown className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{isMarkdown ? t('disableTooltip') : t('enableTooltip')}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
